import { useState, useEffect } from 'react';
import { useApi } from '../../hooks/useApi';
import { FormInput, FormTextArea } from '../forms';

export const IntakeForm = ({ initialData, onSubmit, sessionId }) => {
  const { put } = useApi();
  const [formData, setFormData] = useState({
    presentingProblems: "",
    impactOnFunctioning: {
      current: '',
      past: ''
    },
    diagnosis: "",
    history: {
      pastCounseling: "",
      familyBackground: "",
      medicalHistory: "",
      substanceUseHistory: ""
    },
    interventionsUsed: "",
    nextSteps: "",
    goals: []
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});  // Initialize as empty object instead of null
  const [currentProblem, setCurrentProblem] = useState('');
  const [currentGoal, setCurrentGoal] = useState('');

  useEffect(() => {
    // Only update state if initialData exists and is not null
    if (initialData) {
      setFormData(prevData => ({
        ...prevData,
        ...initialData,
        // Ensure impactOnFunctioning is properly initialized
        impactOnFunctioning: {
          ...prevData.impactOnFunctioning,
          ...(initialData.impactOnFunctioning || {})
        },
        // Ensure history is properly initialized
        history: {
          ...prevData.history,
          ...(initialData.history || {})
        }
      }));
    }
  }, [initialData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});  // Reset errors

    try {
      await onSubmit(formData);
    } catch (err) {
      setErrors({ general: err.message || 'Failed to submit consent form.' });
    } finally {
      setLoading(false);
    }
  };

  const addPresentingProblem = () => {
    if (currentProblem.trim()) {
      setFormData(prev => ({
        ...prev,
        presentingProblems: [...prev.presentingProblems, currentProblem.trim()]
      }));
      setCurrentProblem('');
    }
  };

  const addGoal = () => {
    if (currentGoal.trim()) {
      setFormData(prev => ({
        ...prev,
        goals: [...prev.goals, currentGoal.trim()]
      }));
      setCurrentGoal('');
    }
  };

  return (
    <div className="max-w-4xl mx-auto bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b">
        <h2 className="text-lg font-medium text-gray-900">Initial Counseling Record</h2>
      </div>

      <form onSubmit={handleSubmit} className="p-6 space-y-6">
        {/* Display general error if exists */}
        {errors.general && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            {errors.general}
          </div>
        )}

        {/* Presenting Problems Section */}
        <div className="space-y-4">
          <h3 className="text-md font-medium text-gray-900">Presenting Problems</h3>
          <div className="flex space-x-2">
            <FormTextArea
              placeholder="Describe the presenting problems"
              value={formData.presentingProblems}
              onChange={(e) =>
                setFormData(prev => ({
                  ...prev,
                  presentingProblems: e.target.value
                }))
              }
              rows={4}
            />
          </div>
          <div className="space-y-2">
            {/* {formData.presentingProblems.map((problem, index) => (
              <div key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded">
                <span>{problem}</span>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({
                    ...prev,
                    presentingProblems: prev.presentingProblems.filter((_, i) => i !== index)
                  }))}
                  className="text-red-600 hover:text-red-800"
                >
                  Remove
                </button>
              </div>
            ))} */}
          </div>
        </div>

        {/* Impact on Functioning */}
        <div className="space-y-4">
          <h3 className="text-md font-medium text-gray-900">Impact on Functioning</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormTextArea
              label="Current Functioning"
              value={formData.impactOnFunctioning?.current || ''}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                impactOnFunctioning: {
                  ...prev.impactOnFunctioning,
                  current: e.target.value
                }
              }))}
              error={errors.impactOnFunctioning?.current}
            />
            <FormTextArea
              label="Past Functioning"
              value={formData.impactOnFunctioning?.past || ''}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                impactOnFunctioning: {
                  ...prev.impactOnFunctioning,
                  past: e.target.value
                }
              }))}
              error={errors.impactOnFunctioning?.past}
            />
          </div>
        </div>

        {/* History Sections */}
        <div className="space-y-4">
          <FormTextArea
            label="Past Counseling Experience"
            value={formData.history?.pastCounseling || ''}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              history: {
                ...prev.history,
                pastCounseling: e.target.value
              }
            }))}
          />

          <FormTextArea
            label="Family Background"
            value={formData.history?.familyBackground || ''}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              history: {
                ...prev.history,
                familyBackground: e.target.value
              }
            }))}
          />

          <FormTextArea
            label="Medical History"
            value={formData.history?.medicalHistory || ''}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              history: {
                ...prev.history,
                medicalHistory: e.target.value
              }
            }))}
          />

          <FormTextArea
            label="Substance Use History"
            value={formData.history?.substanceUseHistory || ''}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              history: {
                ...prev.history,
                substanceUseHistory: e.target.value
              }
            }))}
          />
        </div>

        {/* Assessment and Diagnosis Section */}
        <div className="space-y-4">
          <h3 className="text-md font-medium text-gray-900">Assessment and Diagnosis</h3>
          <FormTextArea
            label="Assessment and Diagnosis"
            value={formData.diagnosis || ''}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              diagnosis: e.target.value
            }))}
          />
        </div>

        {/* Goals Section */}
        <div className="space-y-4">
          <h3 className="text-md font-medium text-gray-900">Counseling Goals</h3>
          <div className="flex space-x-2">
            <FormInput
              value={currentGoal}
              onChange={(e) => setCurrentGoal(e.target.value)}
              placeholder="Enter a counseling goal"
              className="flex-1"
            />
            <button
              type="button"
              onClick={addGoal}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Add Goal
            </button>
          </div>
          <div className="space-y-2">
            {formData.goals.map((goal, index) => (
              <div key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded">
                <span>{goal}</span>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({
                    ...prev,
                    goals: prev.goals.filter((_, i) => i !== index)
                  }))}
                  className="text-red-600 hover:text-red-800"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Treatment Section */}
        <div className="space-y-4">
          <h3 className="text-md font-medium text-gray-900">Treatment Plan</h3>
          
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-gray-700">Interventions Used</h4>
            {['CBT', 'Solution Focused', 'Mindfulness', 'Motivational Interviewing', 'Couple Therapy', 'Family Counseling'].map(intervention => {
              const isSelected = Array.isArray(formData.interventionsUsed) 
                ? formData.interventionsUsed.some(i => i.name === intervention)
                : false;
                
              const currentNote = isSelected
                ? formData.interventionsUsed.find(i => i.name === intervention)?.note || ''
                : '';

              return (
                <div key={intervention} className="border rounded-md p-3 bg-gray-50">
                  <div className="flex items-center mb-2">
                    <input
                      type="checkbox"
                      id={`intake-${intervention}`}
                      checked={isSelected}
                      onChange={() => {
                        let newInterventions;
                        if (isSelected) {
                          newInterventions = formData.interventionsUsed.filter(i => i.name !== intervention);
                        } else {
                          newInterventions = [...(Array.isArray(formData.interventionsUsed) ? formData.interventionsUsed : []), { name: intervention, note: '' }];
                        }
                        setFormData(prev => ({ ...prev, interventionsUsed: newInterventions }));
                      }}
                      className="h-4 w-4 text-blue-600 rounded border-gray-300"
                    />
                    <label htmlFor={`intake-${intervention}`} className="ml-2 text-sm font-medium text-gray-700">
                      {intervention}
                    </label>
                  </div>
                  
                  {isSelected && (
                    <div className="ml-6">
                      <FormInput
                        placeholder="Add specific notes for this intervention..."
                        value={currentNote}
                        onChange={(e) => {
                          const newInterventions = formData.interventionsUsed.map(i => 
                            i.name === intervention ? { ...i, note: e.target.value } : i
                          );
                          setFormData(prev => ({ ...prev, interventionsUsed: newInterventions }));
                        }}
                        className="text-sm"
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        <div className="space-y-4">
          <FormTextArea
            label="Next Steps"
            value={formData.nextSteps || ''}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              nextSteps: e.target.value
            }))}
          />
        </div>

        {/* Submit Button */}
        <div className="flex justify-end pt-6">
          <button
            type="submit"
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save Initial Assessment'}
          </button>
        </div>
      </form>
    </div>
  );
};